import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useData } from "../context/DataContext";
import { api, mediaUrl } from "../api";
import useSeo from "../hooks/useSeo";

const PageHead = ({ title, subtitle, image, imagePosition = "center" }) => (
  <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 overflow-hidden bg-[#211d18]">
    {image && <img src={mediaUrl(image)} alt={typeof title === "string" ? `${title} — HAAS Mosaic` : "HAAS Mosaic"} loading="eager" decoding="async" style={{ objectPosition: imagePosition }} className="absolute inset-0 w-full h-full object-cover" />}
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/45" />
    <div className="relative z-10 max-w-[1360px] mx-auto px-5 md:px-8 text-center">
      <h1 className="font-serif text-4xl md:text-6xl text-white mb-4" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.55)" }}>{title}</h1>
      {subtitle && <p className="text-white/90 text-[15px] md:text-[16px] leading-relaxed max-w-2xl mx-auto" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.5)" }}>{subtitle}</p>}
    </div>
  </section>
);

const ProductCard = ({ p }) => {
  const { ui } = useLang();
  return (
    <Link to={`/urun/${p.slug}`} className="group block">
      <div className="card-img-zoom relative overflow-hidden bg-white aspect-square mb-4">
        <img src={mediaUrl(p.image)} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 bg-[#f7f3ee] text-[#211d18] text-[11px] uppercase tracking-[0.16em] px-5 py-2.5">{ui.quickView}</span>
        </div>
      </div>
      <h3 className="font-serif text-xl text-[#211d18] group-hover:text-[#a67c52] transition-colors">{p.name}</h3>
      <p className="text-[#8b8271] text-[12px] uppercase tracking-[0.14em] mt-1">{ui.priceOnRequest}</p>
    </Link>
  );
};

const Shop = () => {
  const { slug } = useParams();
  const { tr, ui } = useLang();
  const { categories } = useData();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeCat = categories.find((c) => c.slug === slug);
  useSeo({
    title: activeCat ? tr(activeCat.name) : (slug ? ui.nav.categories : ui.nav.shop),
    description: activeCat ? tr(activeCat.desc) : { en: "Explore HAAS Mosaic collections — handcrafted marble mosaic artworks across portrait, mythology, 3D, wall, floor, pool, tiles and raw stones.", tr: "HAAS Mosaic koleksiyonlarını keşfedin — portre, mitoloji, 3D, duvar, zemin, havuz, karo ve ham taş kategorilerinde el yapımı mermer mozaik eserler." },
  });

  useEffect(() => {
    setLoading(true);
    api.products(slug).then(setList).catch(() => setList([])).finally(() => setLoading(false));
  }, [slug]);

  return (
    <div>
      <PageHead
        title={activeCat ? tr(activeCat.name) : ui.nav.shop}
        subtitle={activeCat ? tr(activeCat.desc) : ui.projectsIntro}
        image={activeCat ? activeCat.image : "/mosaic/2026/gallery-hero.jpg"}
        imagePosition="center"
      />

      <div className="sticky top-[68px] z-30 bg-[#f7f3ee]/95 backdrop-blur-md border-b border-[#e6ddd0]">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 py-4 flex gap-3 overflow-x-auto no-scrollbar">
          <Link to="/shop" className={`whitespace-nowrap text-[12px] uppercase tracking-[0.14em] px-4 py-2 transition-colors ${!slug ? "bg-[#211d18] text-white" : "text-[#5a5348] hover:text-[#a67c52]"}`}>{ui.viewAll}</Link>
          {categories.map((c) => (
            <Link key={c.slug} to={`/product-category/${c.slug}`} className={`whitespace-nowrap text-[12px] uppercase tracking-[0.14em] px-4 py-2 transition-colors ${slug === c.slug ? "bg-[#211d18] text-white" : "text-[#5a5348] hover:text-[#a67c52]"}`}>{tr(c.name)}</Link>
          ))}
        </div>
      </div>

      <section className="py-16 md:py-20 bg-[#f7f3ee] min-h-[40vh]">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          {loading ? (
            <p className="text-center text-[#8b8271] py-20">…</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
              {list.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </section>

      <section className="pb-24 bg-[#f7f3ee]">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="bg-[#211d18] text-white px-8 md:px-16 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-serif text-3xl md:text-4xl mb-2">{ui.ctaTitle}</h3>
              <p className="text-white/70 text-[15px]">{ui.ctaText}</p>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-3 bg-[#a67c52] hover:bg-[#8f6844] text-white text-[13px] uppercase tracking-[0.16em] px-8 py-4 transition-colors whitespace-nowrap">
              {ui.requestQuote} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shop;
export { PageHead };
